import React from "react";
import { Box, Flex, Heading, useTheme } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SECTIONS } from "../App";

const SectionMotion = motion(Box);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  id?: string; // Optional explicit ID
  maxW?: string | number; // 添加 maxW 屬性，設為可選
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  id: explicitId,
  maxW, // 解構 maxW
}) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  // If an explicit ID is provided, use it, otherwise determine based on title
  const id =
    explicitId ||
    (() => {
      // Check current language
      const isEnglish = i18n.language === "en";

      // English checks
      if (isEnglish) {
        if (
          title.toLowerCase().includes("background") ||
          title.toLowerCase().includes("about")
        )
          return "about";
        if (title.toLowerCase().includes("founder")) return "founder";
        if (
          title.toLowerCase().includes("terms") ||
          title.toLowerCase().includes("t&c")
        )
          return "tnc";
        if (title.toLowerCase().includes("privacy")) return "privacy";
        if (title.toLowerCase().includes("contact")) return "contact";
      }
      // Chinese checks
      else {
        if (title.includes("背景"))
          return title.includes("公司") ? "about" : "founder";
        if (title.includes("T&C") || title.includes("條款")) return "tnc";
        if (title.includes("私障") || title.includes("隔私")) return "privacy";
        if (title.includes("聯絡") || title.includes("联系")) return "contact";
      }

      // Default fallback for other cases
      return title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    })();

  return (
    <SectionMotion as="section" id={id}>
      <Box
        maxW={maxW || { base: "95vw", md: "800px" }} // 使用傳入的 maxW，否則回退到原有的預設值
        mx="auto"
        py={{ base: 6, md: 10 }}
        px={{ base: 4, md: 8 }}
        my={{ base: 4, md: 8 }}
        bg="rgba(255, 255, 255, 0.78)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.72)"
        borderRadius={{ base: "xl", md: "2xl" }}
        backdropFilter="blur(18px)"
        boxShadow={{ base: "md", md: "xl" }}
      >
        <Flex align="center" mb={{ base: 4, md: 6 }}>
          <Box
            w={1.5}
            h={{ base: 6, md: 8 }}
            bg={(() => {
              // Get different accent colors based on section id
              switch (id) {
                case SECTIONS.ABOUT:
                  return theme.colors.primary[500];
                case SECTIONS.FOUNDER:
                  return theme.colors.mood.happy;
                case SECTIONS.LEGAL: // This corresponds to TNC in the app
                  return theme.colors.grey[700];
                case SECTIONS.PRIVACY:
                  return theme.colors.mood.calm;
                case SECTIONS.CONTACT:
                  return theme.colors.mood.unhappy;
                case SECTIONS.HERO:
                  return theme.colors.primary[600];
                case SECTIONS.TUTORIAL:
                  return theme.colors.primary[300];
                case SECTIONS.FEATURES:
                  return theme.colors.mood.calm;
                case SECTIONS.FAQ:
                  return theme.colors.grey[500];
                default:
                  return theme.colors.primary[300];
              }
            })()}
            borderRadius="md"
            mr={{ base: 2, md: 4 }}
          ></Box>
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl" }}
            fontFamily="'Inter', sans-serif"
            color="#23272F"
          >
            {title}
          </Heading>
        </Flex>
        {children}
      </Box>
    </SectionMotion>
  );
};

export default Section;
